import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  applyLocalChecklistItemMutation,
  applyLocalCompletionMutation,
  applyLocalTaskMutation,
  applyLocalTaskTagMutation,
  applyLocalTagMutation,
  applyLocalProjectMutation,
  createTaskLocally,
  deleteTaskPermanently,
  deleteTagPermanently,
  deleteProjectPermanently,
  type LocalMutationContext,
  type TaskMutationPatch,
} from '../../sync/lib/mutations';
import { db } from '../../sync/db/database';
import { refreshTasks } from '../model/tasks.store';
import { useAuthStore } from '../../auth/model/auth.store';
import { showToast, showToastWithAction } from '../../../shared/ui/toast';
import { createUuid } from '../../../shared/lib/uuid';
import { DEFAULT_TAG_COLOR } from '../../../shared/lib/constants';
import { isoDateInTimezone, nowIso } from '../../../shared/lib/datetime';
import type { TaskPriority } from '../../../shared/lib/datetime';
import type { ProjectEntity, TagEntity, TaskEntity } from '../../../entities/task/model/task.types';
import type { TaskListItem } from '../model/task.types';
import { hasRecurrence } from '../model/task.types';
import type { TaskDraft } from '../model/taskDraft';
import { serializeRecurrenceRule } from '../model/recurrence';
import { describeTaskError } from './taskErrors';
import { useUserDates } from './useUserDates';

export interface CreateQuickTaskInput {
  title: string;
  priority?: TaskPriority;
  dueDate: string | null;
  dueTime: string | null;
  allDay?: boolean;
  tagNames?: string[];
  tagIds?: string[];
  projectId?: string | null;
}

export interface SaveTaskDraftOptions {
  /** Черновик относится к идее (is_idea=true). */
  isIdea?: boolean;
}

/** Синхронизация чек-листа с выполнением задачи (все пункты ✓ → задача ✓). */
async function syncChecklistWithCompletion(
  context: LocalMutationContext,
  taskId: string,
  completed: boolean,
): Promise<void> {
  const items = await db.checklist_items.where('task_id').equals(taskId).toArray();
  for (const item of items) {
    if (item.deleted_at !== null) {
      continue;
    }
    if (item.done !== completed) {
      await applyLocalChecklistItemMutation(context, item.id, {
        task_id: taskId,
        text: item.text,
        done: completed,
        sort_order: item.sort_order,
      });
    }
  }
}

function findExistingTagByName(all: TagEntity[], name: string): TagEntity | null {
  const lower = name.toLowerCase();
  return (
    all.find((tag) => tag.deleted_at === null && tag.name.toLowerCase() === lower) ?? null
  );
}

function findExistingProjectByName(all: ProjectEntity[], name: string): ProjectEntity | null {
  const lower = name.toLowerCase();
  return (
    all.find(
      (project) => project.deleted_at === null && project.name.toLowerCase() === lower,
    ) ?? null
  );
}

/**
 * Авто-архив выполненных «до сегодня»:
 * completed=true, archived_at=null, is_idea=false;
 * due_date < today ИЛИ (due_date=null AND дата completed_at < today).
 */
export async function archiveCompletedBeforeToday(
  ctx: LocalMutationContext,
  today: string,
  timezone: string,
): Promise<number> {
  const all = await db.tasks.toArray();
  const targets = all.filter((task) => {
    if (task.deleted_at !== null || task.archived_at !== null || task.is_idea) {
      return false;
    }
    // Повторяющиеся не архивируем по task.completed — у них completions по датам.
    if (task.recurrence_rule !== null && task.recurrence_rule.trim().length > 0) {
      return false;
    }
    if (!task.completed) {
      return false;
    }
    if (task.due_date !== null) {
      return task.due_date < today;
    }
    if (task.completed_at !== null) {
      return isoDateInTimezone(task.completed_at, timezone) < today;
    }
    return false;
  });

  for (const task of targets) {
    await applyLocalTaskMutation(ctx, task.id, { archived_at: nowIso() });
  }
  return targets.length;
}

const AUTO_ARCHIVE_FLAG = 'auto_archive_date';

/**
 * Раз в день при маунте: архивирует выполненные задачи с due_date < сегодня
 * (и undated, выполненные раньше сегодня). Флаг — localStorage `auto_archive_date`.
 */
export function useAutoArchiveCompleted(): void {
  const user = useAuthStore((state) => state.user);
  const { today, timezone } = useUserDates();
  const ranFor = useRef<string | null>(null);

  useEffect(() => {
    if (user === null) {
      return;
    }
    const flagKey = `${user.id}:${today}`;
    if (ranFor.current === flagKey) {
      return;
    }
    if (localStorage.getItem(AUTO_ARCHIVE_FLAG) === flagKey) {
      ranFor.current = flagKey;
      return;
    }
    ranFor.current = flagKey;

    void (async () => {
      try {
        const count = await archiveCompletedBeforeToday(
          { userId: user.id },
          today,
          timezone,
        );
        localStorage.setItem(AUTO_ARCHIVE_FLAG, flagKey);
        if (count > 0) {
          await refreshTasks();
          showToast(
            count === 1
              ? 'Выполненная задача перенесена в архив'
              : `В архив перенесено задач: ${count}`,
            'info',
          );
        }
      } catch {
        // Не блокируем UI: попробуем в следующий маунт/день.
      }
    })();
  }, [user, today, timezone]);
}

/**
 * Высокоуровневые действия над задачами:
 * всё идёт через sync mutations (Dexie + outbox), без обхода outbox.
 */
export function useTaskActions() {
  const user = useAuthStore((state) => state.user);
  const { today } = useUserDates();

  const ctx: LocalMutationContext | null = useMemo(
    () => (user === null ? null : { userId: user.id }),
    [user],
  );

  const run = useCallback(
    async (
      action: (context: LocalMutationContext) => Promise<void>,
      options: { successMessage?: string } = {},
    ): Promise<boolean> => {
      if (ctx === null) {
        showToast('Пользователь не авторизован', 'error');
        return false;
      }
      try {
        await action(ctx);
        await refreshTasks();
        if (options.successMessage !== undefined && options.successMessage.length > 0) {
          showToast(options.successMessage, 'success');
        }
        return true;
      } catch (error) {
        showToast(describeTaskError(error), 'error');
        return false;
      }
    },
    [ctx],
  );

  const toggleTaskCompleted = useCallback(
    async (item: TaskListItem): Promise<void> => {
      await run(async (context) => {
        const task = item.task;
        if (item.isRecurring) {
          // occurrence = день списка (item.occurrenceDate), не start_date серии
          const occurrence = item.occurrenceDate ?? today;
          if (!/^\d{4}-\d{2}-\d{2}$/u.test(occurrence)) {
            showToast('Не удалось определить дату повторения', 'error');
            return;
          }
          const completing = !item.isCompleted;
          if (completing) {
            await applyLocalCompletionMutation(context, task.id, occurrence);
          } else {
            await applyLocalCompletionMutation(context, task.id, occurrence, { deleted: true });
          }
          // Чек-лист → completion серии: пункты синхронизируются с выполнением дня.
          await syncChecklistWithCompletion(context, task.id, completing);
          return;
        }
        const completed = !task.completed;
        await applyLocalTaskMutation(context, task.id, {
          completed,
          completed_at: completed ? nowIso() : null,
        });
        // Чек-лист → выполнение: отметка задачи синхронизирует пункты.
        await syncChecklistWithCompletion(context, task.id, completed);
      });
    },
    [run, today],
  );

  const archiveTask = useCallback(
    async (taskId: string): Promise<boolean> => {
      return run(
        async (context) => {
          await applyLocalTaskMutation(context, taskId, { archived_at: nowIso() });
        },
        { successMessage: 'Задача перенесена в архив' },
      );
    },
    [run],
  );

  /** Restore: только снять archived_at, due_date серии не сдвигаем (отображение from_date=today). */
  const restoreTask = useCallback(
    async (task: TaskEntity): Promise<boolean> => {
      return run(
        async (context) => {
          await applyLocalTaskMutation(context, task.id, { archived_at: null });
        },
        {
          successMessage:
            task.recurrence_rule !== null && task.recurrence_rule.trim().length > 0
              ? 'Серия восстановлена'
              : 'Задача восстановлена',
        },
      );
    },
    [run],
  );

  const deleteTaskForever = useCallback(
    async (taskId: string): Promise<void> => {
      await run(
        async (context) => {
          await deleteTaskPermanently(context, taskId);
        },
        { successMessage: 'Задача удалена навсегда' },
      );
    },
    [run],
  );

  /** Без тостов — для bulk-операций в архиве. */
  const restoreTaskSilently = useCallback(
    async (task: TaskEntity): Promise<boolean> => {
      return run(async (context) => {
        await applyLocalTaskMutation(context, task.id, { archived_at: null });
      });
    },
    [run],
  );

  const deleteTaskForeverSilently = useCallback(
    async (taskId: string): Promise<boolean> => {
      return run(async (context) => {
        await deleteTaskPermanently(context, taskId);
      });
    },
    [run],
  );

  const createQuickTask = useCallback(
    async (input: CreateQuickTaskInput): Promise<boolean> => {
      if (ctx === null) {
        showToast('Пользователь не авторизован', 'error');
        return false;
      }
      try {
        const title = input.title.trim();
        if (title.length === 0) {
          showToast('Введите название задачи', 'error');
          return false;
        }
        const taskId = createUuid();
        const processedTagIds = new Set<string>();
        await createTaskLocally(ctx, {
          id: taskId,
          title,
          priority: input.priority ?? 'medium',
          due_date: input.dueDate,
          due_time: input.dueDate === null ? null : input.dueTime,
          all_day: input.allDay ?? false,
          is_idea: false,
          project_id: input.projectId ?? null,
        });

        for (const name of input.tagNames ?? []) {
          const trimmed = name.trim();
          if (trimmed.length === 0) {
            continue;
          }
          const tagId = await findOrCreateTagByName(ctx, trimmed);
          if (processedTagIds.has(tagId)) {
            continue;
          }
          processedTagIds.add(tagId);
          await applyLocalTaskTagMutation(ctx, taskId, tagId);
        }
        for (const tagId of input.tagIds ?? []) {
          if (processedTagIds.has(tagId)) {
            continue;
          }
          processedTagIds.add(tagId);
          await applyLocalTaskTagMutation(ctx, taskId, tagId);
        }

        await refreshTasks();
        showToast('Задача добавлена', 'success');
        return true;
      } catch (error) {
        showToast(describeTaskError(error), 'error');
        return false;
      }
    },
    [ctx],
  );

  /** Идея: title + is_idea=true, без приоритета/даты в UI (priority medium на сервере ок). */
  const createIdea = useCallback(
    async (title: string): Promise<boolean> => {
      if (ctx === null) {
        showToast('Пользователь не авторизован', 'error');
        return false;
      }
      const trimmed = title.trim();
      if (trimmed.length === 0) {
        showToast('Введите текст идеи', 'error');
        return false;
      }
      try {
        await createTaskLocally(ctx, {
          id: createUuid(),
          title: trimmed,
          priority: 'medium',
          due_date: null,
          due_time: null,
          all_day: false,
          is_idea: true,
        });
        await refreshTasks();
        showToast('Идея добавлена', 'success');
        return true;
      } catch (error) {
        showToast(describeTaskError(error), 'error');
        return false;
      }
    },
    [ctx],
  );

  /** Удаление идеи (tombstone через sync). */
  const deleteIdea = useCallback(
    async (taskId: string): Promise<void> => {
      await run(
        async (context) => {
          await deleteTaskPermanently(context, taskId);
        },
        { successMessage: 'Идея удалена' },
      );
    },
    [run],
  );

  /**
   * Сохранение черновика.
   * Для идей: если заполнен приоритет/дата/проект/теги/повторение/чек-лист → promote (is_idea=false),
   * тост «Задача создана» + undo; иначе остаётся идеей (project_id сбрасывается).
   */
  const saveTaskDraft = useCallback(
    async (
      taskId: string,
      draft: TaskDraft,
      previous: { tagIds: string[]; checklist: TaskDraft['checklist'] },
      options: SaveTaskDraftOptions = {},
    ): Promise<boolean> => {
      const title = draft.title.trim();
      if (title.length === 0) {
        showToast('Укажите название задачи', 'error');
        return false;
      }

      let recurrenceRule: string | null = null;
      if (draft.recurrenceEnabled && draft.recurrence !== null) {
        recurrenceRule = serializeRecurrenceRule(draft.recurrence);
      }

      // Серии нужен якорь: без due_date occursOnDate не умеет считать интервал.
      let dueDate = draft.dueDate;
      if (recurrenceRule !== null && dueDate === null) {
        dueDate = today;
      }

      const isIdeaMode = options.isIdea === true;
      const hasTaskParams =
        draft.priority !== 'medium' ||
        draft.dueDate !== null ||
        draft.projectId !== null ||
        draft.tagIds.length > 0 ||
        recurrenceRule !== null ||
        draft.checklist.some((item) => item.text.trim().length > 0);
      const promote = isIdeaMode && hasTaskParams;

      let successMessage = 'Задача сохранена';
      if (isIdeaMode) {
        successMessage = promote ? 'Задача создана' : 'Идея сохранена';
      }

      const ok = await run(
        async (context) => {
          const patch: TaskMutationPatch = {
            title,
            description: draft.description,
            priority: draft.priority,
            due_date: dueDate,
            due_time: draft.allDay ? null : draft.dueTime,
            duration_minutes: draft.durationMinutes,
            all_day: draft.allDay,
            recurrence_rule: recurrenceRule,
            project_id: isIdeaMode && !promote ? null : draft.projectId,
          };

          if (isIdeaMode) {
            patch.is_idea = !promote;
            if (!promote) {
              patch.priority = 'medium';
              patch.due_date = null;
              patch.due_time = null;
              patch.duration_minutes = null;
              patch.all_day = false;
              patch.recurrence_rule = null;
              patch.project_id = null;
            }
          }

          await applyLocalTaskMutation(context, taskId, patch);

          // Теги/чек-лист применяем только для задач (в т.ч. promote из идеи).
          if (!isIdeaMode || promote) {
            const added = draft.tagIds.filter((id) => !previous.tagIds.includes(id));
            const removed = previous.tagIds.filter((id) => !draft.tagIds.includes(id));
            for (const tagId of added) {
              await applyLocalTaskTagMutation(context, taskId, tagId);
            }
            for (const tagId of removed) {
              await applyLocalTaskTagMutation(context, taskId, tagId, { deleted: true });
            }

            const previousById = new Map(previous.checklist.map((item) => [item.id, item]));
            let sortOrder = 0;
            for (const item of draft.checklist) {
              const prev = previousById.get(item.id);
              if (prev !== undefined) {
                previousById.delete(item.id);
                if (prev.text !== item.text || prev.done !== item.done || prev.sortOrder !== sortOrder) {
                  await applyLocalChecklistItemMutation(context, item.id, {
                    task_id: taskId,
                    text: item.text,
                    done: item.done,
                    sort_order: sortOrder,
                  });
                }
              } else {
                await applyLocalChecklistItemMutation(context, item.id, {
                  task_id: taskId,
                  text: item.text,
                  done: item.done,
                  sort_order: sortOrder,
                });
              }
              sortOrder += 1;
            }
            for (const leftover of previousById.values()) {
              await applyLocalChecklistItemMutation(context, leftover.id, {}, { deleted: true });
            }

            // Для recurring task.completed из чек-листа НЕ выставляем — только completions.
            const current = await db.tasks.get(taskId);
            const isRecurringNow =
              recurrenceRule !== null ||
              (current !== undefined && hasRecurrence(current));
            const finalItems = draft.checklist.filter((item) => item.text.trim().length > 0);
            if (finalItems.length > 0 && !promote && !isRecurringNow) {
              const allDone = finalItems.every((item) => item.done);
              const isCompletedNow = current?.completed === true;
              if (allDone && !isCompletedNow) {
                await applyLocalTaskMutation(context, taskId, {
                  completed: true,
                  completed_at: nowIso(),
                });
              } else if (!allDone && isCompletedNow) {
                await applyLocalTaskMutation(context, taskId, {
                  completed: false,
                  completed_at: null,
                });
              }
            }
          }
        },
        { successMessage },
      );

      if (ok && promote && ctx !== null) {
        // Undo promote: revert к идее на 6 секунд.
        showToastWithAction(
          'Идея оформлена как задача',
          'info',
          {
            label: 'Отменить',
            onClick: () => {
              void (async () => {
                try {
                  await applyLocalTaskMutation(ctx, taskId, {
                    is_idea: true,
                    priority: 'medium',
                    due_date: null,
                    due_time: null,
                    duration_minutes: null,
                    all_day: false,
                    recurrence_rule: null,
                    project_id: null,
                  });
                  await refreshTasks();
                  showToast('Оформление отменено — снова идея', 'success');
                } catch (error) {
                  showToast(describeTaskError(error), 'error');
                }
              })();
            },
          },
          6000,
        );
      }

      return ok;
    },
    [run, ctx],
  );

  const createTag = useCallback(
    async (name: string, color: string = DEFAULT_TAG_COLOR): Promise<string | null> => {
      if (ctx === null) {
        showToast('Пользователь не авторизован', 'error');
        return null;
      }
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        showToast('Введите название тега', 'error');
        return null;
      }
      try {
        const all = await db.tags.toArray();
        const existing = findExistingTagByName(all, trimmed);
        if (existing !== null) {
          await refreshTasks();
          showToast('Тег уже существует — выбран', 'info');
          return existing.id;
        }
        const tagId = createUuid();
        await applyLocalTagMutation(ctx, tagId, { name: trimmed, color });
        await refreshTasks();
        showToast(`Тег «${trimmed}» создан`, 'success');
        return tagId;
      } catch (error) {
        showToast(describeTaskError(error), 'error');
        return null;
      }
    },
    [ctx],
  );

  const updateTag = useCallback(
    async (tagId: string, patch: { name?: string; color?: string }): Promise<boolean> => {
      return run(
        async (context) => {
          await applyLocalTagMutation(context, tagId, patch);
        },
        { successMessage: 'Тег обновлён' },
      );
    },
    [run],
  );

  /** Жёсткое удаление тега (только из меню тегов): связи с задач снимаются, задачи остаются. */
  const deleteTag = useCallback(
    async (tagId: string, tagName: string): Promise<void> => {
      await run(
        async (context) => {
          await deleteTagPermanently(context, tagId);
        },
        { successMessage: `Тег «${tagName}» удалён` },
      );
    },
    [run],
  );

  /** Снять тег с задачи (unlink task_tag), сам тег не удалять. */
  const unlinkTaskTag = useCallback(
    async (taskId: string, tagId: string): Promise<void> => {
      await run(async (context) => {
        await applyLocalTaskTagMutation(context, taskId, tagId, { deleted: true });
      });
    },
    [run],
  );

  const createProject = useCallback(
    async (name: string, color: string = DEFAULT_TAG_COLOR): Promise<string | null> => {
      if (ctx === null) {
        showToast('Пользователь не авторизован', 'error');
        return null;
      }
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        showToast('Введите название проекта', 'error');
        return null;
      }
      try {
        const all = await db.projects.toArray();
        const existing = findExistingProjectByName(all, trimmed);
        if (existing !== null) {
          await refreshTasks();
          showToast('Проект уже существует — выбран', 'info');
          return existing.id;
        }
        const projectId = createUuid();
        await applyLocalProjectMutation(ctx, projectId, { name: trimmed, color });
        await refreshTasks();
        showToast(`Проект «${trimmed}» создан`, 'success');
        return projectId;
      } catch (error) {
        showToast(describeTaskError(error), 'error');
        return null;
      }
    },
    [ctx],
  );

  const updateProject = useCallback(
    async (projectId: string, patch: { name?: string; color?: string }): Promise<boolean> => {
      return run(
        async (context) => {
          await applyLocalProjectMutation(context, projectId, patch);
        },
        { successMessage: 'Проект обновлён' },
      );
    },
    [run],
  );

  /** Жёсткое удаление проекта: у задач project_id=null, проект — tombstone. */
  const deleteProject = useCallback(
    async (projectId: string, projectName: string): Promise<void> => {
      await run(
        async (context) => {
          await deleteProjectPermanently(context, projectId);
        },
        { successMessage: `Проект «${projectName}» удалён` },
      );
    },
    [run],
  );

  return {
    userId: user?.id ?? null,
    createTag,
    updateTag,
    deleteTag,
    unlinkTaskTag,
    createProject,
    updateProject,
    deleteProject,
    createQuickTask,
    createIdea,
    deleteIdea,
    saveTaskDraft,
    archiveTask,
    restoreTask,
    restoreTaskSilently,
    deleteTaskForever,
    deleteTaskForeverSilently,
    toggleTaskCompleted,
  };
}

async function findOrCreateTagByName(
  ctx: LocalMutationContext,
  name: string,
): Promise<string> {
  const all = await db.tags.toArray();
  const existing = findExistingTagByName(all, name);
  if (existing !== null) {
    // Молча переиспользуем: прикрепление существующего тега — норма, не ошибка.
    return existing.id;
  }
  const tagId = createUuid();
  await applyLocalTagMutation(ctx, tagId, { name, color: DEFAULT_TAG_COLOR });
  return tagId;
}

export type { TagEntity, ProjectEntity };
